/**
 * The bar at the end of the draft: what is wrong, what to check, and the
 * Publish and Discard buttons (spec 2026-10-23 §5). Publish is disabled while
 * nothing changed, and the reason is always written next to it — a disabled
 * button with no reason is a dead end.
 */
import { AlertTriangle, Send, Undo2, XCircle } from 'lucide-react';
import type { IntroPublishState } from '../lib/intro-state';

/** What the bar reads. The intro and the Abby welcome tab both give it. */
export type PublishBarState = Pick<IntroPublishState, 'dirty' | 'canPublish' | 'errors' | 'warnings' | 'blockedReason'>;

interface Props {
  state: PublishBarState;
  /** A version was published before. */
  hasLive: boolean;
  /** The intro is on now: Publish reaches members. */
  liveOn: boolean;
  canWrite: boolean;
  busy: boolean;
  /** Why the admin must wait (an upload is running); wins over every other reason. */
  waitReason: string | null;
  onPublish: () => void;
  onDiscard: () => void;
  /** Start of the element ids; each tab needs its own. The default keeps the intro tab's ids. */
  idPrefix?: string;
  /** What Publish does now, in one sentence. The default is the intro's sentence. */
  effect?: string;
}

export function PublishBar({ state, hasLive, liveOn, canWrite, busy, waitReason, onPublish, onDiscard, idPrefix = 'intro', effect }: Props) {
  const reasonId = `${idPrefix}-publish-reason`;
  const effectText =
    effect ??
    (liveOn
      ? 'The intro is on. When you publish, new installs get this version.'
      : 'The intro is off. Publish saves this version. Members do not see it until you turn the intro on.');

  return (
    <section aria-label="Publish" className="space-y-3 rounded-lg border bg-card p-5">
      {state.errors.length > 0 && (
        <ul aria-live="polite" className="space-y-1 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
          {state.errors.map((e) => (
            <li key={e} className="flex gap-2">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{e}</span>
            </li>
          ))}
        </ul>
      )}
      {state.dirty && state.warnings.length > 0 && (
        <ul className="space-y-1 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {state.warnings.map((w) => (
            <li key={w} className="flex gap-2">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
              <span>{w}</span>
            </li>
          ))}
        </ul>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm">
          <p className="font-medium">
            {state.dirty ? 'You have changes that are not published.' : hasLive ? 'This is the newest published version.' : 'Nothing is published yet.'}
          </p>
          <p id={reasonId} className="text-xs text-muted-foreground">
            {waitReason ?? state.blockedReason ?? effectText}
          </p>
        </div>
        {canWrite && (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={onDiscard}
              disabled={busy || !state.dirty}
              className="inline-flex items-center gap-1.5 rounded-md border px-4 py-2 text-sm font-medium hover:bg-muted disabled:opacity-50"
            >
              <Undo2 className="h-4 w-4" aria-hidden="true" />
              Discard changes
            </button>
            <button
              type="button"
              onClick={onPublish}
              disabled={busy || waitReason !== null || !state.canPublish}
              aria-describedby={reasonId}
              className="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
              Publish
            </button>
          </div>
        )}
      </div>
    </section>
  );
}
